const post = {
  slug: "checkout-dm-pricing-and-plans-explained",
  title: "Checkout DM Pricing and Plans: What Each Plan Includes",
  seoTitle: "Checkout DM Pricing and Plans Explained",
  description:
    "Checkout DM has five plans, from Free to Pro, with no transaction fees. See what each plan costs, what it includes and how to choose the right one.",
  excerpt:
    "Free, Creator, Starter, Growth and Pro: what each Checkout DM plan costs, what changes as you move up and how to pick the one that fits your shop.",
  category: "Product",
  tags: ["Checkout DM pricing", "Checkout DM plans", "Instagram DM automation pricing"],
  date: "2026-10-28T12:00:00Z",
  cover: {
    src: "/blog-images/checkout-dm-pricing-and-plans-explained-cover.png",
    alt: "A phone showing the five Checkout DM plans from Free to Pro",
  },
  blocks: [
    {
      type: "p",
      text:
        "Before you connect your Instagram account to any tool, you want to know two things: what it costs and what you get for the money. This guide walks through all five Checkout DM plans, Free, Creator, Starter, Growth and Pro, so you can see exactly where each one starts and stops.",
    },
    {
      type: "p",
      text:
        "Two things are the same on every plan. Checkout DM takes **no commission and no transaction fee** on your orders, and payments go straight to your own payment account or bank account. You pay for the plan, not for each sale.",
    },

    { type: "h2", text: "Prices at a glance" },
    {
      type: "table",
      headers: ["Plan", "Monthly", "Yearly (pay for 10 months)", "Built for"],
      rows: [
        ["Free", "\u20B90 ($0)", "\u2014", "Trying DM selling with real customers, no card needed"],
        ["Creator", "\u20B9499 ($9)", "\u20B94,990 ($90)", "Creators and affiliate marketers selling digital products"],
        ["Starter", "\u20B91,499 ($19)", "\u20B914,990 ($190)", "Sellers who sell every day and have outgrown Free"],
        ["Growth", "\u20B92,899 ($39)", "\u20B928,990 ($390)", "Turning commenters into customers and automating marketing"],
        ["Pro", "\u20B96,499 ($69)", "\u20B964,990 ($690)", "Running Instagram like a full storefront with a team"],
      ],
    },
    {
      type: "callout",
      tone: "peach",
      title: "Always check the live prices",
      text:
        "These are the prices on the [Pricing page](/pricing) at the time of writing. Plans and prices can change, so treat that page as the final word.",
    },
    {
      type: "figure",
      src: "/blog-images/checkout-dm-pricing-and-plans-explained-figure.png",
      alt: "The five Checkout DM plans with monthly prices in rupees and the main limit or feature of each",
      caption: "The five plans and what defines each one.",
      width: 1200,
      height: 1035,
    },

    { type: "h2", text: "What every plan includes" },
    {
      type: "ul",
      items: [
        "**Full DM checkout automation on Instagram**, with the catalog, cart and payment link handled inside the chat.",
        "**Payments your way.** UPI, bank transfer and QR code with manual confirmation, or Razorpay, Stripe and PayPal for automatic confirmation. You can keep one automatic gateway active at a time.",
        "**Your return policy, exchange policy and FAQs** shown automatically in DMs.",
        "**Order and return emails** sent to your customers.",
        "**Digital delivery.** Every plan can deliver a digital product by link after payment, although the Creator plan sells digital products only.",
        "**Email support.**",
      ],
    },

    { type: "h2", text: "Free: try it with real customers" },
    {
      type: "p",
      text:
        "Free lets you list up to **5 products** and handle **15 unique customer conversations per month**. It includes the core DM checkout, manual and gateway payments, automatic return and exchange handling, a shipping fee at checkout and shipped emails. Checkout DM branding is shown to customers in DMs, checkout and emails. If you pass the monthly conversation limit, you are asked to upgrade to keep handling new conversations that month.",
    },

    { type: "h2", text: "Creator: for digital products and affiliate links" },
    {
      type: "p",
      text:
        "Creator is a **digital-only** plan with up to **150 products** and **unlimited conversations**. It adds Comment-to-DM automation, free digital products you can give away, a Preview or Buy button for affiliate links and a public Storefront with one-link checkout, so people can buy without messaging you first. Creator removes Checkout DM branding from DMs but keeps it in emails and on the Storefront. If you are curious how this works in practice, read [how to sell digital products on Instagram](/blog/sell-digital-products-on-instagram).",
    },

    { type: "h2", text: "Starter: the first plan for physical goods at scale" },
    {
      type: "p",
      text:
        "Starter includes everything in Free and Creator, raises the limit to **500 products** and removes Checkout DM branding everywhere. It also adds the tools that save time on a busy day:",
    },
    {
      type: "ul",
      items: [
        "Abandoned-cart information for you, so you can follow up yourself.",
        "Take over any chat at any time.",
        "Automatic delivery status updates (Delivered, RTO, Failed) when your courier is connected.",
        "Automatic stock updates on every sale, return or exchange.",
        "Automatic free shipping above a minimum cart value.",
        "Reminders for unshipped orders and your company address in emails.",
      ],
    },

    { type: "h2", text: "Growth: marketing on autopilot" },
    {
      type: "p",
      text:
        "Growth raises the catalog to **1,000 products** and focuses on getting more from the traffic you already have. Story replies are matched to the exact product shown in the Story. You also get automatic and manual coupons, time-based and count-based offers, a multi-step automatic abandoned-cart recovery sequence, low-stock alerts per size, review requests sent after a number of days you choose, Dead Stock Insights, a Trending Products category inside DMs, CSV export of customers, orders and returns, revenue and conversion analytics and priority support.",
    },

    { type: "h2", text: "Pro: for bigger catalogs and teams" },
    {
      type: "p",
      text:
        "Pro includes everything in Growth with **10,000 products**, three team members (one admin and two users, with extra seats available), Google-indexable product pages, bestseller products, bulk photo upload of up to 50 images at once, quantity-based discounts, cross-sell suggestions in order and shipped emails, real-time webhooks for orders and returns, advanced analytics and dedicated support. Pro also includes a one-to-one onboarding call with the founder.",
    },

    { type: "h2", text: "Key differences side by side" },
    {
      type: "table",
      headers: ["Feature", "Free", "Creator", "Starter", "Growth", "Pro"],
      rows: [
        ["Catalog products", "5", "150 (digital only)", "500", "1,000", "10,000"],
        ["Customer conversations a month", "15", "Unlimited", "Unlimited", "Unlimited", "Unlimited"],
        ["Comment-to-DM automation", "No", "Yes", "Yes", "Yes", "Yes"],
        ["Checkout DM branding", "Shown", "Only in emails", "Removed", "Removed", "Removed"],
        ["Delivery status tracking", "No", "No", "Yes", "Yes, with re-check", "Yes, with re-check"],
        ["Abandoned carts", "No", "No", "Alert for you", "Automatic recovery", "Automatic recovery"],
        ["Coupons and offers", "No", "No", "No", "Yes", "Yes, plus quantity offers"],
        ["Story replies", "Generic", "Generic", "Generic", "Product-matched", "Product-matched"],
        ["Team members", "1", "1", "1", "1", "3, with extra seats"],
      ],
    },

    { type: "h2", text: "How to choose a plan" },
    {
      type: "ol",
      items: [
        "**Selling digital files or affiliate links only?** Creator is built for exactly that. It does not allow physical products.",
        "**Selling physical products and just testing?** Start on Free. The limits are 5 products and 15 conversations a month, so you will know quickly whether DM checkout suits your shop.",
        "**Getting more than a handful of conversations or products?** Starter removes the conversation limit and the branding, and adds delivery tracking and stock updates.",
        "**Want to run offers and recover abandoned carts automatically?** Those arrive with Growth.",
        "**Managing a large catalog or a team?** Pro is where team seats and the 10,000-product limit are.",
      ],
    },
    {
      type: "p",
      text:
        "If you are weighing Checkout DM against other tools, our [honest comparison of free Instagram automation tools](/blog/best-free-instagram-automation-tools) and the [Checkout DM vs Shopify guide](/blog/checkout-dm-vs-shopify-instagram-sellers) show where each option fits.",
    },

    { type: "h2", text: "Changing or leaving a plan" },
    {
      type: "p",
      text:
        "You can move between plans at any time without losing your products, orders or customer history, and there is no long-term contract. Yearly billing gives you two months free, because you pay for ten months instead of twelve.",
    },
  ],
  related: [
    { to: "/pricing", title: "Pricing", description: "Current plans, prices and the full feature comparison." },
    { to: "/faq", title: "Frequently Asked Questions", description: "Answers on plans, payments, shipping and returns." },
    { to: "/help", title: "Help Guide", description: "How to set up and run your Checkout DM account." },
  ],
  faqs: [
    {
      q: "How much does Checkout DM cost?",
      a: "There are five plans. Monthly prices are Free at \u20B90, Creator at \u20B9499, Starter at \u20B91,499, Growth at \u20B92,899 and Pro at \u20B96,499. Yearly billing gives two months free. Always check the Pricing page for the current numbers.",
    },
    {
      q: "Does Checkout DM charge a commission on my sales?",
      a: "No. There is no commission or transaction fee on any plan, and payments go directly to your own payment account or bank account.",
    },
    {
      q: "What are the limits of the Free plan?",
      a: "Free includes up to 5 products and 15 unique customer conversations per month. Customers see Checkout DM branding in DMs, checkout and emails on this plan.",
    },
    {
      q: "Can I sell physical products on the Creator plan?",
      a: "No. Creator is digital-only. Free, Starter, Growth and Pro can sell both physical and digital products.",
    },
    {
      q: "Can I change my plan later?",
      a: "Yes, at any time. You can move between plans without losing your products, orders or customer history, and there is no long-term contract.",
    },
  ],
};

export default post;
